// MAX edit sheet — kg/lb input + unit toggle + Clear. Presentational:
// the owner (MaxesView via useMaxEditor) holds the edit state + the
// PUT/DELETE orchestration and passes handlers in. Chrome (Cancel·Title·Save
// header, full-screen page-sheet, keyboard-avoiding body, safe-area) comes from
// the shared <PageSheet>; the safe-area insets are injected as props so it
// renders on native and the web-rnw console alike (react-native-web has no
// safe-area, passes 0).
import { useTranslation } from "react-i18next";

import { PageSheet } from "../primitives/PageSheet";
import type { UiTheme } from "../theme";
import { MaxEditForm } from "./MaxEditForm";
import type { WeightUnit } from "./useMaxEditor";

type EditMaxSheetProps = {
  theme: UiTheme;
  visible: boolean;
  exerciseName: string;
  value: string;
  onChangeValue: (next: string) => void;
  unit: WeightUnit;
  onToggleUnit: () => void;
  busy: "save" | "clear" | null;
  error: string | null;
  canClear: boolean;
  onCancel: () => void;
  onSave: () => void;
  onClear: () => void;
  /** Safe-area insets (px) — the mobile mount feeds useSafeAreaInsets so the
   *  full-screen Android Modal clears the status bar / home indicator; web
   *  passes none (its Modal overlay needs no insets). */
  topInset?: number;
  bottomInset?: number;
};

export function EditMaxSheet({
  theme,
  visible,
  exerciseName,
  value,
  onChangeValue,
  unit,
  onToggleUnit,
  busy,
  error,
  canClear,
  onCancel,
  onSave,
  onClear,
  topInset = 0,
  bottomInset = 0,
}: EditMaxSheetProps) {
  const { t } = useTranslation();

  return (
    <PageSheet
      theme={theme}
      visible={visible}
      onRequestClose={onCancel}
      title={exerciseName}
      cancelLabel={t("mobile.maxes.editCancel")}
      onCancel={onCancel}
      cancelDisabled={busy !== null}
      action={{
        label: t("mobile.maxes.editSave"),
        onPress: onSave,
        loading: busy === "save",
        disabled: busy !== null,
      }}
      keyboardAvoiding
      topInset={topInset}
      bottomInset={bottomInset}
    >
      <MaxEditForm
        theme={theme}
        value={value}
        onChangeValue={onChangeValue}
        unit={unit}
        onToggleUnit={onToggleUnit}
        busy={busy}
        error={error}
        canClear={canClear}
        onClear={onClear}
        autoFocus
      />
    </PageSheet>
  );
}
